/**
 * Created by Nikolai Gorte
 * User: n.gorte on 10.08.2016
 * Copyright: xdot GmbH 2016
 */

'use strict';

import React from 'external/react';
import PropTypes from 'external/prop-types';
import { Accordion, Badge, Button, Card, Col, Row, Table } from 'external/react-bootstrap';
import { ClassNames as ChangeRecordClassNames } from 'plugin/change-record-portlet';
import {
    AppContext,
    ConfigActions,
    DataLinkContainer,
    DataUtil,
    FlagComponent,
    flagHOC,
    getLocalConfigStore,
    Title
} from 'plugin/liferay-widgets-react-web';
import { UserHistoryEntry } from 'plugin/user-state-history-portlet';
import { FeatureDisplayPlugin, Map, PrintPlugin, transformGeometryToFeatures } from 'plugin/map-portlet';
import { UserMultiSubscriptionButton } from 'plugin/pub-sub-portlet';

import ModalActions from '../actions/ModalActions';
import FliwasPermissionActions from '../actions/FliwasPermissionActions';
import Icon from './Icon';
import { ElementTypes, GROUP_NAMED_LISTS } from '../constants/Constants';
import AttachedDocumentList from './AttachedDocumentList';
import SaveToNamedListDropdown from './SaveToNamedListDropdown';
import DateTimeDisplay from './DateTimeDisplay';
import ImageDisplay from './ImageDisplay';
import ItemDescription from './ItemDescription';
import MultilineText from './MultilineText';
import AttachedObjectList from './AttachedObjectList';
import formatMeasurements from '../utils/formating-util';
import { getAssetLinkStore, getFliwasPermissionStore, getNamedListStore } from '../stores/StoreFactory';
import { formatDateTimeNoSeconds } from '../utils/date-format';

const LocalConfigStore = getLocalConfigStore(AppContext);
const NamedListStore = getNamedListStore(AppContext);
const FLiwasPermissionStore = getFliwasPermissionStore(AppContext);

class DetailsBox extends React.Component {

    constructor(props) {
        super(props);
        let expanded = undefined;
        this.key = 'DetailsBox_' + this.props.className + '_' + this.props.title;
        const localState = LocalConfigStore.getLocalConfig(this.key);
        if (localState) {
            expanded = localState.properties;
        }

        this.state = {
            expanded: (expanded !== undefined ? expanded : (this.props.defaultExpanded || true))
        };

        this.onToggle = this.onToggle.bind(this);
    }

    onToggle(eventKey) {
        const newExpanded = !this.state.expanded;
        if (this.props.toggle && eventKey !== 'journalEntry') {
            ConfigActions.updateLocalConfig(this.key, newExpanded);
            this.setState({ expanded: newExpanded });
        }
    }

    render() {
        const { title, titleIcon, onTitleIconClick, children, subscription, toggle = false, fastEditFunc } = this.props;
        const { expanded } = this.state;
        const defaultActiveKey = toggle ? (expanded ? 'accordionToggle' : '') : 'accordionToggle';

        return (
            <Accordion
                activeKey={defaultActiveKey}
                onSelect={this.onToggle}
            >
                <Card>
                    <Accordion.Toggle as={Card.Header} eventKey={'accordionToggle'}>
                        <Card.Title>
                            {title} {titleIcon && <Icon name={titleIcon} onClick={onTitleIconClick}/>}
                            {subscription && <UserMultiSubscriptionButton {...subscription} />}
                            {fastEditFunc &&
                                <Button
                                    variant="outline-primary"
                                    className="pull-right details-renderer-fast-edit-button"
                                    title="Bearbeiten"
                                    onClick={fastEditFunc}
                                >
                                    <Icon name="pencil" title="Bearbeiten"/>
                                </Button>
                            }
                        </Card.Title>
                    </Accordion.Toggle>

                    <Accordion.Collapse eventKey={'accordionToggle'}>
                        <Card.Body>
                            <div className="details-box">
                                {expanded && children}
                            </div>
                        </Card.Body>
                    </Accordion.Collapse>
                </Card>
            </Accordion>
        );
    }
}

const DetailsBoxPropertyLine = props => {
    const { stateStyle, value, userMeta, timestamp, icon, onClick, single = false } = props;

    let iconElement;
    if (icon) {
        iconElement = typeof icon === 'string' ? <Icon name={icon}/> : icon;
    }

    return (
        <Row onClick={onClick ? onClick : undefined} className={'clickable'}>
            {iconElement &&
                <Col lg={2} md={3} sm={3} xs={4}
                     className={'details-box-icons ' + (single ? ' single' : '')}>
                    {iconElement}
                </Col>}
            <Col
                lg={iconElement ? 10 : 12}
                md={iconElement ? 9 : 12}
                sm={iconElement ? 9 : 12}
                xs={iconElement ? 8 : 12}
                className={'details-box-content ' + (single ? ' single' : '')}
            >
                {stateStyle && <Badge className={'description ' + stateStyle}>{value}</Badge>}
                {!stateStyle && <span className="description">{value}</span>}
                {!userMeta && timestamp && <DateTimeDisplay timestamp={timestamp} classNames="date"/>}
                {userMeta && <UserHistoryEntry userMeta={userMeta} timestamp={timestamp}/>}
            </Col>
        </Row>);
};

const DetailsBoxSingleProperty = props => {
    return (
        <DetailsBox {...props}>
            <DetailsBoxPropertyLine {...props} single={true}/>
        </DetailsBox>);
};

const DetailsBoxAttachedDocuments = ({ title, item, className }) => {
    return (
        <DetailsBox title={title} toggle={true} className={className}>
            <AttachedDocumentList list={item}/>
        </DetailsBox>);
};

const DetailsBoxAttachedObjects = ({ title, item, className }) => {
    return (
        <DetailsBox title={title} toggle={true} className={className}>
            <AttachedObjectList list={item} size={180}/>
        </DetailsBox>);
};

// eslint-disable-next-line react/prop-types
const TextPropertyRenderer = ({ value, transformFn, prefix, suffix, multiline = false }) => {
    let returnVal = value ? value : '---';
    if (transformFn) {
        returnVal = transformFn(value);
    }

    if (prefix) {
        returnVal += prefix + ' ';
    }

    if (suffix) {
        returnVal += ' ' + suffix;
    }

    if (multiline) {
        return <MultilineText> {value} </MultilineText>;
    }

    return returnVal;
};

const BooleanPropertyRenderer = ({ value }) => {
    return value ? 'Ja' : 'Nein';
};

const TimeValuePairPropertyRenderer = ({ measurement }) => {
    return measurement && (measurement.value || measurement.value === 0) ? ('' + formatMeasurements(measurement.value) + (measurement.unit ? ' ' + measurement.unit + ' (' + formatDateTimeNoSeconds(measurement.timestamp) + ')' : '')) : '---';
};

const ExternalIdsPropertyRenderer = ({ externalIds }) => {
    if (externalIds && externalIds.length > 0) {
        return externalIds.map((extId, idx) => {
            return <div key={idx}>{extId.externalId + ' (' + extId.source + ')'} </div>;
        });
    } else {
        return '---';
    }
};

const ReferenceValuePropertyRenderer = ({ referenceValues }) => {
    if (referenceValues && referenceValues.length > 0) {
        return referenceValues.map((refVal, idx) => {
            return <div
                key={idx}>{refVal.description + ' (' + formatMeasurements(refVal.value) + ' ' + refVal.unit + ')'} </div>;
        });
    } else {
        return '---';
    }
};

const InstructionPropertyRenderer = ({ instructions }) => {
    if (instructions && instructions.length > 0) {
        return instructions.map((instr, idx) => {
            return <div key={idx}>{instr.title + (instr.responsible ? (' (' + instr.responsible + ')') : '')}</div>;
        });
    } else {
        return '---';
    }
};

const InlineTableProperties = ({ title = 'Eigenschaften', children, className, onClick }) => {
    return (
        <DetailsBox className={className} title={title} onClick={onClick} toggle={true}>
            <Table responsive className="noborder">
                <tbody>
                {children}
                </tbody>
            </Table>
        </DetailsBox>
    );
};

class PropertyBox extends React.Component {
    render() {
        // eslint-disable-next-line react/prop-types
        const { inline = false, title, boxGridSize = 4, children } = this.props;
        if (inline) {
            return (<React.Fragment>
                <tr>
                    <td>{title}:</td>
                    <td>{children}</td>
                </tr>
            </React.Fragment>);
        } else {
            return (<React.Fragment>
                <Col lg={boxGridSize} md={boxGridSize} sm={boxGridSize} xs={boxGridSize}>
                    <h4>{title}:</h4>
                    {children}
                </Col>
            </React.Fragment>);
        }
    }
}

class StatusBox extends React.Component {
    render() {
        return (
            <Col lg={4} md={4} sm={6} xs={12}
                 className="details-container">
                {this.props.children}
            </Col>
        );
    }
}

class DynamicContentBox extends React.Component {
    render() {
        return (
            <DetailsBox className={this.props.className} title={this.props.title} toggle={true}>
                {this.props.children}
            </DetailsBox>
        );
    }
}

const DetailsDescriptionRow = props => {
    const { item: dataItem } = props,
        item = DataUtil.asItem(dataItem);

    if (!item) {
        return null;
    }

    let imageUrl = undefined, geometry = undefined;
    if (item && item.topImage) {
        imageUrl = item.topImage && item.topImage.previewUrl ? item.topImage.previewUrl : '/o/fliwas-theme/images/file_system/large/document.png';
    }
    if (item && item.imageUrl) {
        imageUrl = item.imageUrl;
    }
    if (item && item.geometry) {
        geometry = item.geometry;
    }

    return (
        <React.Fragment>
            {(imageUrl || item.description) &&
                <DetailsBox className={props.className} title={'Beschreibung' + (imageUrl ? ' mit Titelbild' : '')}
                            toggle={true}>
                    <Row>
                        <Col md={imageUrl ? 6 : 12} className="nomargin">
                            <ItemDescription item={item}/>
                        </Col>
                        {
                            imageUrl &&
                            <Col md={6} className="nomargin">
                                <ImageDisplay
                                    previewUrl={imageUrl}
                                    className="preview-image"
                                />
                            </Col>
                        }
                    </Row>
                </DetailsBox>}

            {geometry &&
                <DetailsBox className={props.className} title="Karte" toggle={true}>
                    <Map
                        plugins={[ FeatureDisplayPlugin, PrintPlugin ]}
                        defaultFallbackMapConfigType={'static'}
                        features={transformGeometryToFeatures(dataItem)}
                        style={{ height: '400px', cursor: 'pointer' }}
                        zoom={12}
                    />
                </DetailsBox>}
        </React.Fragment>
    );
};

let DetailsBoxAttachedDocumentsHOC = flagHOC(DetailsBoxAttachedDocuments, [ 'item' ]);
let DetailsBoxAttachedObjectsHOC = flagHOC(DetailsBoxAttachedObjects, [ 'item' ]);

// eslint-disable-next-line react/prop-types
const AttachedDocs = ({ documentLinks, className }) => {
    let hasLinkedDocuments = documentLinks && DataUtil.asArray(documentLinks) && DataUtil.asArray(documentLinks).length > 0;
    return (
        <React.Fragment>
            {hasLinkedDocuments &&
                <DetailsBoxAttachedDocumentsHOC item={documentLinks} className={className}/>
            }
        </React.Fragment>
    );
};
// eslint-disable-next-line react/prop-types
const AttachedObjs = ({ objectLinks, className }) => {
    let hasLinkedObjects = objectLinks && DataUtil.asArray(objectLinks) && DataUtil.asArray(objectLinks).length > 0;
    return (
        <React.Fragment>
            {hasLinkedObjects &&
                <DetailsBoxAttachedObjectsHOC item={objectLinks} className={className}/>
            }
        </React.Fragment>
    );
};

let AttachedDocsCHOC = DataLinkContainer.link(AttachedDocs, [ 'documentLinks', 'objectLinks' ], getAssetLinkStore(AppContext));
let AttachedObjsCHOC = DataLinkContainer.link(AttachedObjs, [ 'documentLinks', 'objectLinks' ], getAssetLinkStore(AppContext));

class DetailsRenderer extends React.Component {

    constructor(props) {
        super(props);
        this.DATA = {
            NAMED_LIST_PERMISSNIOM: 'NamedListPermission'
        };

        this.state = {
            namedListPermission: undefined
        };

        this.SaveToNamedListDropdownCHOC = DataLinkContainer.link(SaveToNamedListDropdown, 'namedLists', NamedListStore);

    }

    componentDidMount() {
        FliwasPermissionActions.getPermissionState(this.DATA.NAMED_LIST_PERMISSNIOM, {
            nameSpace: 'named-lists',
            fliwasPermission: 'manage-named-lists'
        });
        this.removeListener = FLiwasPermissionStore.addListener(this.onChange.bind(this));
    }

    openChangeRecordDetails() {
        const item = DataUtil.asItem(this.props.item);

        ModalActions.showModal(ChangeRecordClassNames.ChangeRecord, {
            byClassName: this.props.className,
            byId: item.id,
            start: 0,
            end: AppContext.DEFAULT_LIST_SIZE
        }, true);
    }

    onChange() {
        this.setState({ namedListPermission: FLiwasPermissionStore.getRemoteData(this.DATA.NAMED_LIST_PERMISSNIOM) });
    }

    componentWillUnmount() {
        if (this.removeListener) {
            this.removeListener.remove();
        }
    }

    render() {
        const {
                item: dataItem,
                documentLinks,
                objectLinks,
                children,
                canAddToList = false
            } = this.props,
            item = DataUtil.asItem(dataItem);
        const namedListPermission = this.state.namedListPermission,
            hasNameListPermission = namedListPermission && DataUtil.asItem(namedListPermission) && DataUtil.asItem(namedListPermission).state && DataUtil.asItem(namedListPermission).state.toLowerCase() === 'set';

        const propertyBoxes = React.Children.map(this.props.children, (child) => {
            if (child && child.props && child.props.elementType === ElementTypes.PropertyBox) {
                return child;
            }
        });

        const statusBoxes = React.Children.map(children, (child) => {
            if (child && child.props && child.props.elementType === ElementTypes.StatusBox) {
                return child;
            }
        });

        const dynamicContentBoxes = React.Children.map(children, (child) => {
            if (child && child.props && child.props.elementType === ElementTypes.DynamicContentBox) {
                return child;
            }
        });

        return (
            <div className="details-widget">
                <FlagComponent item={dataItem}>
                    <Title
                        title={this.props.itemTitle || item && item.title}
                        icon={item && <Icon name={this.props.iconClassName || this.props.className}/>}
                        listAdd={
                            canAddToList && hasNameListPermission &&
                            <this.SaveToNamedListDropdownCHOC
                                namedLists={GROUP_NAMED_LISTS}
                                namedListPermission={this.DATA.NAMED_LIST_PERMISSNIOM}
                                dataItem={dataItem}
                            />
                        }
                    />
                    <div className={'details-widget__content padding-box'}>
                        <div className="row">
                            {statusBoxes}
                        </div>
                        {dynamicContentBoxes && dynamicContentBoxes.length > 0 && <div className="nomargin">
                            {dynamicContentBoxes}
                        </div>}
                        <div className="nomargin">
                            <DetailsDescriptionRow item={dataItem} className={dataItem.className}/>
                        </div>
                        <div className="nomargin">
                            {propertyBoxes && propertyBoxes.length > 0 &&
                                <InlineTableProperties onClick={this.openChangeRecordDetails.bind(this)}
                                                       className={dataItem.className}>
                                    {propertyBoxes}
                                </InlineTableProperties>
                            }
                            {documentLinks && typeof documentLinks === 'string' &&
                                <AttachedDocsCHOC documentLinks={documentLinks} className={this.props.className}/>}
                            {documentLinks && typeof documentLinks === 'object' &&
                                <AttachedDocs documentLinks={documentLinks} className={this.props.className}/>}
                            {objectLinks && typeof objectLinks === 'string' &&
                                <AttachedObjsCHOC objectLinks={objectLinks} className={this.props.className}/>}
                            {objectLinks && typeof objectLinks === 'object' &&
                                <AttachedObjs objectLinks={objectLinks} className={this.props.className}/>}
                        </div>
                    </div>
                    <br/>
                </FlagComponent>

            </div>
        );
    }
}

DetailsDescriptionRow.propTypes = {
    item: PropTypes.object.isRequired,
    className: PropTypes.string.isRequired
};

DetailsBoxPropertyLine.propTypes = {
    icon: PropTypes.node,
    stateStyle: PropTypes.object,
    value: PropTypes.node,
    userMeta: PropTypes.object,
    timestamp: PropTypes.number,
    titleContainerRef: PropTypes.object,
    className: PropTypes.string.isRequired
};

DetailsBoxSingleProperty.propTypes = {
    ...DetailsBoxPropertyLine.propTypes,
    icon: PropTypes.node
};

InlineTableProperties.propTypes = {
    title: PropTypes.string,
    children: PropTypes.node,
    propertyDescriptions: PropTypes.object,
    onClick: PropTypes.func,
    className: PropTypes.string.isRequired
};

DetailsBoxAttachedDocuments.propTypes = {
    title: PropTypes.string,
    item: PropTypes.object,
    className: PropTypes.string.isRequired
};
DetailsBoxAttachedObjects.propTypes = {
    title: PropTypes.string,
    item: PropTypes.object,
    className: PropTypes.string.isRequired
};
DynamicContentBox.propTypes = {
    title: PropTypes.string,
    children: PropTypes.node,
    className: PropTypes.string.isRequired
};
StatusBox.propTypes = {
    children: PropTypes.node
};

DetailsBoxAttachedDocuments.defaultProps = {
    title: 'Dokumente'
};

DetailsBoxAttachedObjects.defaultProps = {
    title: 'Verknüpfte Objekte'
};

PropertyBox.defaultProps = {
    elementType: ElementTypes.PropertyBox
};

StatusBox.defaultProps = {
    elementType: ElementTypes.StatusBox
};

DynamicContentBox.defaultProps = {
    elementType: ElementTypes.DynamicContentBox
};

DetailsBox.propTypes = {
    children: PropTypes.node,
    title: PropTypes.oneOfType([
        PropTypes.func,
        PropTypes.string
    ]),
    titleIcon: PropTypes.string,
    onTitleIconClick: PropTypes.func,
    subscription: PropTypes.object,
    toggle: PropTypes.bool,
    defaultExpanded: PropTypes.bool,
    className: PropTypes.string.isRequired,
    fastEditFunc: PropTypes.oneOfType([
        PropTypes.func,
        PropTypes.bool,
    ])
};

DetailsRenderer.propTypes = {
    className: PropTypes.string.isRequired,
    children: PropTypes.node,
    iconClassName: PropTypes.string,
    item: PropTypes.object,
    itemTitle: PropTypes.string,
    documentLinks: PropTypes.oneOfType([
        PropTypes.object,
        PropTypes.string
    ]),
    objectLinks: PropTypes.oneOfType([
        PropTypes.object,
        PropTypes.string
    ]),
    propertyDescriptions: PropTypes.object,
    canAddToList: PropTypes.bool
};

export {
    DetailsRenderer,
    DetailsBoxSingleProperty,
    DetailsBoxAttachedDocuments,
    DetailsBoxPropertyLine,
    DetailsBox,
    PropertyBox,
    StatusBox,
    DynamicContentBox,
    TextPropertyRenderer,
    BooleanPropertyRenderer,
    TimeValuePairPropertyRenderer,
    ExternalIdsPropertyRenderer,
    ReferenceValuePropertyRenderer,
    InstructionPropertyRenderer
};



// WEBPACK FOOTER //
// ./js/components/DetailsRenderer.jsx